import { fetchWithAuth } from './adminApi'

export type MarketStatus =
  | 'draft'
  | 'applications_open'
  | 'applications_closed'
  | 'day_of'
  | 'wrapped'
export type ProgramStatus = 'draft' | 'active' | 'paused' | 'closed'
export type RebateStatus = 'pending' | 'approved' | 'rejected' | 'paid' | 'void'
export type AmountMode = 'full_fee' | 'percent' | 'fixed'
export type RebateBasis = 'booth_price' | 'total_paid'

export interface RebateRules {
  sponsor_name: string
  amount_mode: AmountMode
  percent_bps: number | null
  fixed_cents: number | null
  basis: RebateBasis
  per_vendor_cap_cents: number | null
  budget_cents: number | null
  max_recipients: number | null
  require_check_in: boolean
  min_sales_count: number
  min_sales_cents: number
  sales_grace_hours: number
  include_offline_payments: boolean
  notes: string
}

export interface RebateProgram extends RebateRules {
  id: string
  market_id: string
  status: ProgramStatus
  revision: number
  created_by: string
  updated_by: string
  created_at: string
  updated_at: string
}

export interface RebateTotals {
  pending_count: number
  pending_cents: number
  approved_count: number
  approved_cents: number
  paid_count: number
  paid_cents: number
  rejected_count: number
  void_count: number
  committed_cents: number
  remaining_budget_cents: number | null
  remaining_recipients: number | null
}

export interface RebateCheck {
  key: string
  label: string
  passed: boolean
  detail: string
}

export interface RebateEligibility {
  eligible: boolean
  basis_cents: number
  amount_cents: number
  checks: RebateCheck[]
}

export interface Rebate {
  id: string
  program_id: string
  market_id: string
  application_id: string
  vendor_shop_id: string
  status: RebateStatus
  computed_cents: number
  amount_cents: number
  is_exception: boolean
  eligibility: RebateEligibility
  decision_reason: string | null
  decided_by: string | null
  decided_at: string | null
  payout_method: string | null
  payout_reference: string | null
  payout_note: string | null
  paid_by: string | null
  paid_at: string | null
  void_reason: string | null
  voided_by: string | null
  voided_at: string | null
  revision: number
  created_by: string
  created_at: string
  updated_at: string
}

export interface RebateListItem extends Rebate {
  shop_name: string | null
  shop_handle: string | null
  market_name: string | null
  market_starts_at: string | null
}

export interface RebateHistoryEntry {
  id: string
  actor_id: string
  actor_email: string | null
  action: string
  from_status: string | null
  to_status: string
  amount_cents: number
  detail: Record<string, unknown>
  created_at: string
}

export interface VendorSignals {
  application_id: string
  vendor_shop_id: string
  shop_name: string
  shop_handle: string | null
  category: string
  spot_label: string | null
  tier_name: string | null
  paid_at: string | null
  payment_method: 'stripe' | 'offline'
  checked_in_at: string | null
  no_show_at: string | null
  refunded_cents: number
  booth_price_cents: number
  total_paid_cents: number
  sales_count: number
  sales_cents: number
  first_sale_at: string | null
  overlapping_events: number
}

export interface RebateDetail extends RebateListItem {
  history: RebateHistoryEntry[]
  current: RebateEligibility | null
  signals: VendorSignals | null
  program_status: ProgramStatus
}

export interface VendorRow extends VendorSignals {
  eligibility: RebateEligibility | null
  rebate: Rebate | null
}

export interface VendorRoster {
  program: RebateProgram | null
  totals: RebateTotals
  vendors: VendorRow[]
}

export interface RebatePreview {
  paid_vendors: number
  eligible_count: number
  projected_cents: number
  funded_count: number
  funded_cents: number
  over_budget: boolean
  failed_checks: Record<string, number>
  vendors: VendorRow[]
}

export interface PayoutMethod {
  id: string
  label: string
  reference_required: boolean
}

export interface RebateConfig {
  defaults: RebateRules
  max_rebate_cents: number
  max_budget_cents: number
  max_sales_grace_hours: number
  bulk_limit: number
  require_distinct_payer: boolean
  payout_methods: PayoutMethod[]
}

export interface HostedEventSummary {
  id: string
  name: string
  slug: string
  status: MarketStatus
  city: string | null
  state_code: string | null
  venue_name: string | null
  starts_at: string
  ends_at: string
  vendors_pay: boolean
  organizer_shop_id: string
  organizer_name: string | null
  organizer_handle: string | null
  paid_vendors: number
  checked_in_vendors: number
  rebate_program_status: ProgramStatus | null
  rebate_committed_cents: number
  rebate_pending_count: number
}

export interface HostedEventDetail extends HostedEventSummary {
  address: string | null
  mode: string
  spot_kind: string
  capacity: number | null
  applications_by_status: Record<string, number>
  no_show_vendors: number
  booth_revenue_cents: number
  booth_refunded_cents: number
  program: RebateProgram | null
  totals: RebateTotals
}

export interface HostedEventFilters {
  status?: MarketStatus | ''
  timeframe?: 'upcoming' | 'past' | ''
  search?: string
  rebates?: 'with' | 'without' | ''
  limit?: number
  offset?: number
}

export interface RebateFilters {
  status?: RebateStatus | ''
  market_id?: string
  limit?: number
  offset?: number
}

export interface GenerateResult {
  created: number
  refreshed: number
  existing: number
  ineligible: number
  rejected: number
}

export interface BulkResult {
  succeeded: number
  failed: number
  results: { rebate_id: string; ok: boolean; status: RebateStatus | null; error: string | null }[]
}

export class RebateRequestError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
  // 409 means someone else changed the record or a rule blocked the change; reload before retrying.
  get conflict() {
    return this.status === 409
  }
}

async function request<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const result = await fetchWithAuth<T>(`/admin${path}`, {
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  if (!result.ok || result.data === undefined)
    throw new RebateRequestError(
      result.error?.message || 'Request failed',
      Number(result.error?.code) || 0
    )
  return result.data
}

function query(filters: object) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== '' && value !== null) params.append(key, String(value))
  })
  const text = params.toString()
  return text ? `?${text}` : ''
}

const id = encodeURIComponent

export const hostedEvents = {
  list: (filters: HostedEventFilters = {}) =>
    request<{ items: HostedEventSummary[]; total: number }>(`/hosted-events${query(filters)}`),
  detail: (marketId: string) => request<HostedEventDetail>(`/hosted-events/${id(marketId)}`),
  vendors: (marketId: string) => request<VendorRoster>(`/hosted-events/${id(marketId)}/vendors`),
  saveProgram: (marketId: string, rules: RebateRules, expectedRevision: number | null) =>
    request<RebateProgram>(`/hosted-events/${id(marketId)}/rebate-program`, 'PUT', {
      ...rules,
      expected_revision: expectedRevision,
    }),
  setProgramStatus: (
    marketId: string,
    status: ProgramStatus,
    expectedRevision: number,
    reason: string
  ) =>
    request<RebateProgram>(`/hosted-events/${id(marketId)}/rebate-program/status`, 'POST', {
      status,
      expected_revision: expectedRevision,
      reason,
    }),
  preview: (marketId: string, rules: RebateRules) =>
    request<RebatePreview>(`/hosted-events/${id(marketId)}/rebate-program/preview`, 'POST', rules),
  generate: (marketId: string, refreshPending: boolean) =>
    request<GenerateResult>(`/hosted-events/${id(marketId)}/rebates/generate`, 'POST', {
      refresh_pending: refreshPending,
    }),
  open: (
    marketId: string,
    applicationId: string,
    amountCents: number | null,
    reason: string | null
  ) =>
    request<Rebate>(`/hosted-events/${id(marketId)}/rebates`, 'POST', {
      application_id: applicationId,
      amount_cents: amountCents,
      reason,
    }),
}

export const rebates = {
  config: () => request<RebateConfig>('/event-rebates/config'),
  list: (filters: RebateFilters = {}) =>
    request<{ items: RebateListItem[]; total: number; totals: RebateTotals }>(
      `/event-rebates${query(filters)}`
    ),
  detail: (rebateId: string) => request<RebateDetail>(`/event-rebates/${id(rebateId)}`),
  approve: (
    rebateId: string,
    revision: number,
    amountCents: number | null,
    reason: string | null
  ) =>
    request<Rebate>(`/event-rebates/${id(rebateId)}/approve`, 'POST', {
      expected_revision: revision,
      amount_cents: amountCents,
      reason,
    }),
  reject: (rebateId: string, revision: number, reason: string) =>
    request<Rebate>(`/event-rebates/${id(rebateId)}/reject`, 'POST', {
      expected_revision: revision,
      reason,
    }),
  reopen: (rebateId: string, revision: number, reason: string) =>
    request<Rebate>(`/event-rebates/${id(rebateId)}/reopen`, 'POST', {
      expected_revision: revision,
      reason,
    }),
  pay: (
    rebateId: string,
    revision: number,
    payout: { method: string; reference: string | null; note: string | null }
  ) =>
    request<Rebate>(`/event-rebates/${id(rebateId)}/pay`, 'POST', {
      expected_revision: revision,
      payout_method: payout.method,
      payout_reference: payout.reference,
      payout_note: payout.note,
    }),
  void: (rebateId: string, revision: number, reason: string) =>
    request<Rebate>(`/event-rebates/${id(rebateId)}/void`, 'POST', {
      expected_revision: revision,
      reason,
    }),
  bulk: (action: 'approve' | 'reject', rebateIds: string[], reason: string | null) =>
    request<BulkResult>('/event-rebates/bulk', 'POST', {
      action,
      rebate_ids: rebateIds,
      reason,
    }),
}

export const money = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100)

// Blank and unparseable both return null; callers that treat blank as "no limit" must check isBlank first.
export const toCents = (dollars: string): number | null => {
  const text = dollars.trim().replace(/^\$/, '')
  const plain = /^\d{1,3}(,\d{3})+(\.\d+)?$/.test(text) ? text.replace(/,/g, '') : text
  return /^\d+(\.\d{1,2})?$/.test(plain) ? Math.round(Number(plain) * 100) : null
}

export const isBlank = (text: string) => text.trim() === ''

export const toDollars = (cents: number | null) => (cents === null ? '' : (cents / 100).toFixed(2))
